import { arrayToMap, arrayToObject, groupBy, min } from 'lib/array/arrayUtils'
import clsx from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import HotelMonthTile from './OfferCalendarHotelMonthTile'
import FlightsMonthTile from './OfferCalendarFlightsMonthTile'
import { CalendarPickerMonthView, getCalendarMonths, getMonthsOtherAvailability, getSelectedOfferPageDay, markCheapestPrice } from 'selectors/offerPage/offerPageSelectors'
import { getMultiRoomCalendarMonths, getMultiRoomMonthsOtherAvailability, getSelectedOfferPageMultiRoomDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { INTL_LONG_MONTH, ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import { calendarDateToMoment } from 'lib/datetime/time'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import OfferCalendarPriceBlurb from '../OfferCalendarPriceBlurb'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidUsdCircleIcon from 'LuxKit/Icons/solid/SolidUsdCircleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import Group from 'components/utils/Group'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { isBundleOffer, isVillaOffer } from 'lib/offer/offerTypes'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import ModalContext from 'contexts/ModalContext'
import OfferCalendarPickerRecommendationsModal from '../OfferCalendarPickerRecommendationsModal'
import { isAnyDayInMonthInDiscountPeriod, getBestVillasDiscounts } from 'components/OfferPage/Villas/utils/calculateDiscount'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { INCLUSIONS_TITLE_BONUS } from 'constants/inclusions'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { BundleSelectedDaysInfo, getBundleCalendarMonths, getBundleSelectedDays } from 'selectors/offerPage/bundledOfferPageSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'

const YearWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const MonthTile = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  position: relative;
  text-align: left;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};
  min-height: ${rem(70)};

  > * + * {
    margin-top: ${rem(4)};
  }

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.sold-out {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.neutral.default.one};
  }

`

interface Props {
  className?: string
  monthViews: Array<CalendarPickerMonthView>
  enableCalendarRecommendations: boolean
  monthAvailability: { [month: string]: boolean }
  onMonthSelect?: (monthKey: string) => void
  duration: number
  room?: App.PackageRoomType
  flightsOnly?: boolean
  selectedDay?: App.CalendarDay
  isAnyFlightsActive: boolean
  isBundle?: boolean
  offer: App.Offer
  displayMemberPrice: boolean
  fetching?: boolean
  rooms: Array<App.Occupants>
  markCheapestPrice: boolean
  bundleSelectedDays?: BundleSelectedDaysInfo
  isMultiRoom: boolean
}

function OfferCalendarMonthPicker(props: Props) {
  const {
    className,
    enableCalendarRecommendations,
    monthViews,
    onMonthSelect,
    duration,
    monthAvailability,
    room,
    flightsOnly,
    selectedDay,
    isAnyFlightsActive,
    isBundle,
    offer,
    displayMemberPrice,
    fetching,
    rooms,
    markCheapestPrice,
    bundleSelectedDays,
    isMultiRoom,
  } = props

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const isMobile = useScreenSizeOnly('mobile')

  const showModal = useContext(ModalContext)
  const onMonthClick = useCallback((month: CalendarPickerMonthView) => {
    const hasOtherAvailability = room && !!monthAvailability[month.key]
    const shouldShowRecommendations = flightsOnly && !month.packagedAvailable && !hasOtherAvailability && enableCalendarRecommendations
    if (shouldShowRecommendations) {
      if (isMobile) {
        showModal(<OfferCalendarPickerRecommendationsModal monthIndex={month.key} />)
      } else {
        dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
      }
    } else {
      if (isAnyFlightsActive && !month.packagedAvailable) {
        if (enableCalendarRecommendations && !month.hotelAvailable) {
          if (isMobile) {
            showModal(<OfferCalendarPickerRecommendationsModal monthIndex={month.key} />)
          } else {
            dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
          }
        } else {
          dispatch({ type: OfferPageActions.setFlights, flights: false })
        }
      } else if (month.hotelAvailable) {
        onMonthSelect?.(month.key)
      } else if (room && monthAvailability[month.key]) {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
      } else if (enableCalendarRecommendations) {
        if (isMobile) {
          showModal(<OfferCalendarPickerRecommendationsModal monthIndex={month.key} />)
        } else {
          dispatch({ type: OfferPageActions.showRecommendationsView, monthIndex: month.key })
        }
      }
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.MonthSelect, calendarDateToMoment(month).format(ISO_DATE_SHORT_FORMAT))
  }, [
    dispatch,
    flightsOnly,
    isAnyFlightsActive,
    monthAvailability,
    onMonthSelect,
    onUserAnalyticsAction,
    room,
    enableCalendarRecommendations,
    showModal,
    isMobile,
  ])

  const monthsByYear = useMemo(() => groupBy(monthViews, view => view.year), [monthViews])

  const [monthByPurchasableTactical, yearByPurchasableTactical, yearByCheapestMonth] = useMemo(() => {
    const byYearHasTactical = new Map<string, boolean>()
    const byYearHasCheapest = new Map<string, boolean>()
    const byMonth = new Map<string, boolean>()

    for (const [year, months] of monthsByYear.entries()) {
      for (const month of months) {
        const key = `${month.month} ${year}`
        byMonth.set(key, month.hasTactical)
        byYearHasTactical.set(year, byYearHasTactical.get(year) ? true : month.hasTactical)
        byYearHasCheapest.set(year, byYearHasCheapest.get(year) ? true : month.cheapest)
      }
    }

    return [byMonth, byYearHasTactical, byYearHasCheapest]
  }, [monthsByYear])

  const yearPrices = useMemo(() => {
    return arrayToObject(monthsByYear.entries(),
      ([year]) => year,
      ([_, months]) => {
        const hotelFromPrice = min(months, month => month.hotelAvailable ? month.hotelTotal : Number.MAX_SAFE_INTEGER)?.hotelTotal ?? 0
        const packagedFromPrice = min(months, month => month.packagedAvailable ? month.packagedTotal : Number.MAX_SAFE_INTEGER)?.packagedTotal ?? 0

        return {
          hotelFromPrice,
          packagedFromPrice,
        } })
  }, [monthsByYear])

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  let FireLabel
  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <SolidFireIcon colour="urgency" />
    FireLabel = <BodyTextBlock startIcon={<SolidFireIcon colour="urgency" />} variant="medium" wrap="no-wrap">
      {intl.formatMessage({
        id: 'HHWr1FaGVu',
        defaultMessage: 'Limited Time Special',
        description: 'Label for limited time special promotion in calendar month picker',
      })}
    </BodyTextBlock>
  } else {
    FireIcon = <LineFireIcon colour="highlight-secondary" />
    FireLabel = <BodyTextBlock startIcon={<LineFireIcon colour="highlight-secondary" />} variant="medium" format="titlecase">
      {intl.formatMessage(
        {
          id: 'AxClbzw0LH',
          defaultMessage: '{inclusions} Available',
          description: 'Label for extra inclusions available in calendar month picker',
        },
        { inclusions: INCLUSIONS_TITLE_BONUS },
      )}
    </BodyTextBlock>
  }
  const hideChildPrices = offer.type === 'tactical_ao_hotel' && !!offer.property?.hideChildPrices && rooms?.some(room => room.children !== undefined && room.children > 0)
  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const yearsWithLoyaltyUpgrades = useMemo(() => arrayToMap(
    monthsByYear.entries(),
    ([year]) => year,
    ([_, months]) => canLoyaltyUpgrade ? months.some(month => (!!month.loyalty.availableUpgradeTargetsUniqueKeys.room || !!month.loyalty.availableUpgradeTargetsUniqueKeys.package)) : false,
  ), [canLoyaltyUpgrade, monthsByYear])

  return (
    <VerticalSpacer className={className} gap={24}>
      {Array.from(monthsByYear.entries()).map(([year, months]) => {
        const fromPrice = isAnyFlightsActive ? yearPrices[year].packagedFromPrice : yearPrices[year].hotelFromPrice
        const yearHasCheapest = markCheapestPrice ? yearByCheapestMonth.get(year) ?? false : false
        const yearHasLoyaltyUpgrade = yearsWithLoyaltyUpgrades.get(year)
        const yearHasPurchasableTactical = (yearByPurchasableTactical.get(year) ?? false) && !yearHasLoyaltyUpgrade

        return <VerticalSpacer key={year} gap={12}>
          <Group
            direction="vertical"
            tabletDirection="horizontal"
            horizontalAlign="space-between"
            verticalAlign="end"
            gap={4}
          >
            <Group direction="vertical">
              <Heading variant="heading3">{year}</Heading>
              {months.some(month => month.loading) && <TextLoadingBox typography="body-medium" width={280} />}
              {Number.isFinite(fromPrice) && fromPrice > 0 && !isBundle && <OfferCalendarPriceBlurb
                year={year}
                fromPrice={fromPrice}
                cheapest={yearHasCheapest}
              />}
            </Group>
            <Group direction="horizontal" gap={8}>
              {yearHasPurchasableTactical && FireLabel}
              {yearHasLoyaltyUpgrade && <CaptionTextBlock
                variant="medium"
                startIcon={<SolidCircleIcon size="XS" colour={`tier-${tier}`} />}
                colour="neutral-two"
              >
                {intl.formatMessage({
                  id: 'BFtA64u5T6',
                  defaultMessage: 'Free upgrade available',
                  description: 'Label indicating free loyalty upgrade is available for a year in calendar month picker',
                })}
              </CaptionTextBlock>}
            </Group>
          </Group>
          <YearWrapper>
            {months.map((month) => {
              const available = flightsOnly ? month.packagedAvailable : month.hotelAvailable
              const hasOtherAvailability = room && !!monthAvailability[month.key] && !isBundle
              const disabled = (!available && !hasOtherAvailability && !enableCalendarRecommendations) || month.loading
              const selected = selectedDay?.monthKey === month.key
              const key = `${month.month} ${year}`
              const monthHasPurchasableTactical = monthByPurchasableTactical.get(key) ?? false
              const monthName = intlDateFormatter(new Date(month.yearNum, month.monthIndex), INTL_LONG_MONTH)

              const hasVillasDiscount = month.hotelAvailable && isVillaOffer(offer) && offer.discounts && offer.discounts.length > 0
              const bestVillasDiscounts = hasVillasDiscount && getBestVillasDiscounts(offer.discounts ?? [])
              const showVillasDiscount = bestVillasDiscounts &&
                isAnyDayInMonthInDiscountPeriod(bestVillasDiscounts, calendarDateToMoment(month), duration)
              const hasLoyaltyUpgrade = canLoyaltyUpgrade && (!!month.loyalty.availableUpgradeTargetsUniqueKeys.room || !!month.loyalty.availableUpgradeTargetsUniqueKeys.package)
              const showFlightDeal = !!(month.hasFlightDeal && isAnyFlightsActive) && !hasLoyaltyUpgrade

              let soldOutText: string | undefined
              if (isVillaOffer(offer)) {
                soldOutText = intl.formatMessage({
                  id: 'o/j3YDjpkN',
                  defaultMessage: 'Check-in unavailable',
                  description: 'Text shown when check-in is unavailable for a month in calendar month picker',
                })
              } else {
                soldOutText = undefined
              }

              return <MonthTile
                className={clsx({ 'sold-out': !available, selected })}
                onClick={() => onMonthClick(month)}
                key={month.month}
                disabled={disabled}
                data-testid={clsx({
                  'month-tile': month.hotelAvailable,
                  'month-tile-with-flights': month.packagedAvailable,
                })}
              >
                <Group fullWidth direction="horizontal" horizontalAlign="space-between">
                  <HeadingTextBlock
                    variant="heading6"
                    endIcon={<>
                      {showFlightDeal && <SolidFireIcon size="S" colour="urgency" />}
                      {hasLoyaltyUpgrade && <SolidCircleIcon size="S" colour={`tier-${tier}`} />}
                    </>}
                  >
                    {monthName}
                  </HeadingTextBlock>
                  {month.hasPromo && <SolidUsdCircleIcon size="S" colour={selectedDay?.monthKey !== month.key ? 'highlight-secondary' : undefined} />}
                  {!month.hasPromo && monthHasPurchasableTactical && FireIcon}
                  {showVillasDiscount && <SolidFireIcon size="S" colour="urgency" />}
                </Group>
                {(month.loading || fetching) && <VerticalSpacer gap={4}>
                  <TextLoadingBox typography="caption-medium" width="100%" />
                  <TextLoadingBox typography="body-large" width="100%" />
                </VerticalSpacer>}
                {!(month.loading || fetching) && <>
                  {isAnyFlightsActive && <FlightsMonthTile
                    flightsOnly={flightsOnly}
                    month={month}
                    duration={bundleSelectedDays?.duration ?? duration}
                    selected={selected}
                    enableCalendarRecommendations={enableCalendarRecommendations}
                    hasPromo={month.hasPromo}
                    displayMemberPrice={displayMemberPrice}
                    isMultiRoom={isMultiRoom}
                  />}
                  {!isAnyFlightsActive && <HotelMonthTile
                    month={month}
                    duration={bundleSelectedDays?.duration ?? duration}
                    selected={selected}
                    hasOtherAvailability={!!hasOtherAvailability}
                    enableCalendarRecommendations={enableCalendarRecommendations}
                    soldOutText={soldOutText}
                    hideChildPrices={hideChildPrices}
                    markCheapestPrice={markCheapestPrice}
                    isMultiRoom={isMultiRoom}
                  />}
                </>}
                {month.limitedAvailability && <UrgencyLabel variant="critical1">
                  {intl.formatMessage({
                    id: 'c9gY/LLlox',
                    defaultMessage: 'Limited availability',
                    description: 'Urgency label for limited availability in calendar month picker',
                  })}
                </UrgencyLabel>}
              </MonthTile>
            })}
          </YearWrapper>
        </VerticalSpacer>
      })}
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  const isBundle = isBundleOffer(currentState.offer)

  let monthViews
  if (isMultiRoom) {
    monthViews = getMultiRoomCalendarMonths(currentState, appState).months
  } else if (isBundleOffer(currentState.offer)) {
    monthViews = getBundleCalendarMonths(state, appState).months
  } else {
    monthViews = getCalendarMonths(appState, currentState).months
  }

  return {
    bundleSelectedDays: isBundle ? getBundleSelectedDays(appState, currentState) : undefined,
    isBundle,
    airport: state.airport,
    duration: currentState.duration,
    enableCalendarRecommendations: !isBundle ? shouldEnableCalendarRecommendations(currentState.offer) : false,
    flights: state.flights,
    offer: state.offer,
    flightsOnly: state.offer?.bundledWithFlightsOnly,
    monthAvailability: isMultiRoom ? getMultiRoomMonthsOtherAvailability(state, appState) : getMonthsOtherAvailability(appState, state),
    monthViews,
    room: state.room,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    isAnyFlightsActive: state.flights,
    displayMemberPrice: state.displayMemberPrice,
    rooms: state.rooms,
    markCheapestPrice: markCheapestPrice(state),
    open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
    isMultiRoom,
  }
}, OfferCalendarMonthPicker, preventRenderWithClosedFilter)
